import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popup, Button, Icon, Label } from 'semantic-ui-react';
import styled from 'styled-components';

import { useCredits } from 'src/hooks/useCredits';
import OutOfCreditsModal from './OutOfCreditsModal';

const ChipLabel = styled(Label)`
  cursor: pointer;
`;

const PopupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;

  .plan-line {
    font-size: 0.85em;
    color: var(--br-muted);
  }
`;

type CreditBalanceChipProps = {
  // Optional route to the usage/credits page; falls back to no-op when absent.
  usageHref?: string;
};

const CreditBalanceChip: React.FC<CreditBalanceChipProps> = ({ usageHref }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data: credits } = useCredits();
  const [modalOpen, setModalOpen] = useState(false);

  const balance = credits?.balance ?? 0;

  const openModal = () => setModalOpen(true);

  const trigger = (
    <ChipLabel color="yellow" data-testid="credit-balance-chip">
      <Icon name="lightning" />
      {t('NCredits', '{{count}} credits', { count: balance })}
    </ChipLabel>
  );

  return (
    <>
      <Popup on="click" trigger={trigger} position="bottom right">
        <PopupBody>
          <strong>
            {t('NCredits', '{{count}} credits', { count: balance })}
          </strong>
          {credits?.resetDate && (
            <span className="plan-line">
              {credits?.productInternalId === 'free'
                ? t('FreePlan', 'Free plan')
                : t('PremiumPlan', 'Premium plan')}{' '}
              ·{' '}
              {t('ResetsOn', 'resets {{date}}', {
                date: new Date(credits.resetDate).toLocaleDateString(),
              })}{' '}
              · {credits?.monthlyCredits ?? 0}/{t('Month', 'mo')}
            </span>
          )}
          <Button size="tiny" color="orange" onClick={openModal}>
            {t('BuyMoreCredits', 'Buy more credits')}
          </Button>
          {usageHref && (
            <Button size="tiny" basic onClick={() => history.push(usageHref)}>
              {t('ViewUsageHistory', 'View usage history')}
            </Button>
          )}
        </PopupBody>
      </Popup>
      <OutOfCreditsModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default CreditBalanceChip;
